import { IApiError } from "services/api/api.types";
import { useToast } from "components/atoms/toast/toast-context";
import { ICreateParentPayload } from "services/api/users/user-services.types";
import { useAuth } from "./../../../../stores/auth/auth-context";
import { constants, phoneRegExp } from "constants/constants";
import { fetchSchoolBySearchParams } from "services/api/school/schoolService";
import {
  IUseUpdateUserProfileForm,
  OptionType,
  UpdateUserFormSchema,
  UserCitySource
} from "./../use-profile-form.types";
import {
  fetchCityBySearchParams,
  getCityFromGPSCoordinates
} from "services/api/city/cityService";
import { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  createParentDetails,
  updateUserProfile
} from "services/api/users/user-services";
import { CountryCode } from "types/common/common.types";
import trackEvent from "services/mixpanel/mixpanel";
import { eventNames } from "services/mixpanel/events";
import {
  createSuperPropertiesForTrackingEvent,
  getSourceInfoEvent
} from "utils/mixpanel/mixpanel-utils";
import { parseAPIErrorResponse } from "utils/utils";
import { useGeolocation } from "hooks/use-geolocation/use-geolocation";
import { useBoolean } from "hooks/use-boolean/use-boolean";
import { IFetchCityByGPSCoordinatesResponse } from "services/api/city/cityService.types";
import { useGetCurrentPlatform } from "hooks/use-media-query/use-get-currrent-platform";
import * as Sentry from "@sentry/nextjs";

const FormSchema = yup.object().shape({
  city: yup.string().required("Silakan masukkan nama kota"),
  school: yup.string().required("Silakan masukkan nama sekolah"),
  parentNumber: yup
    .string()
    .required("Mohon lengkapi nomor HP kamu")
    .matches(phoneRegExp, "Masukin nomor HP yang aktif, ya.")
    .min(8, "Masukin minimal 8 angka, ya.")
    .max(13, "Hmm... nomornya kepanjangan, nih.")
});

const useUpdateUserProfileForm =
  (): IUseUpdateUserProfileForm<UpdateUserFormSchema> => {
    const [cityList, setCityList] = useState<OptionType[]>([]);
    const [schoolList, setSchoolList] = useState<OptionType[]>([]);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const { addToast } = useToast();

    const [isLoading, setIsLoading] = useBoolean();
    const [locationSource, setLocationSource] = useState<UserCitySource | null>(
      null
    );

    const [reverseGeoCodeResponse, setReverseGeoCodeResponse] =
      useState<IFetchCityByGPSCoordinatesResponse["data"]>(null);

    const {
      user,
      user: { userData }
    } = useAuth();

    const { primary_user } = userData;

    const currentPlatform = useGetCurrentPlatform();

    useEffect(() => {
      trackEvent({
        eventName: eventNames.additionalDetailsScreenViewed,
        payload: {
          ...createSuperPropertiesForTrackingEvent({ useAuthData: user }),
          sourceInfo: getSourceInfoEvent(currentPlatform)
        }
      });
    }, []);

    const { access_token, student_id } = primary_user;

    const {
      control,
      handleSubmit,
      formState: { isValid },
      setValue,
      getValues
    } = useForm<UpdateUserFormSchema>({
      defaultValues: {
        parentNumber: "",
        city: "",
        school: ""
      },
      mode: "onChange",
      delayError: constants.DELAY_ERROR,
      resolver: yupResolver(FormSchema)
    });

    const { getLocation, geoLocationData } = useGeolocation();

    const getGPSData = async () => {
      trackEvent({
        eventName: eventNames.locationPopupViewed,
        payload: {
          ...createSuperPropertiesForTrackingEvent({ useAuthData: user }),
          sourceInfo: getSourceInfoEvent(currentPlatform)
        }
      });
      await getLocation();
    };

    useEffect(() => {
      if (
        geoLocationData &&
        geoLocationData.latitude !== null &&
        geoLocationData.longitude !== null
      ) {
        trackEvent({
          eventName: eventNames.locationPermissionGranted,
          payload: {
            ...createSuperPropertiesForTrackingEvent({ useAuthData: user }),
            sourceInfo: getSourceInfoEvent(currentPlatform)
          }
        });

        getCityNameFromGPS(
          geoLocationData.latitude,
          geoLocationData.longitude,
          access_token
        );
      }
      if (geoLocationData && geoLocationData.error) {
        trackEvent({
          eventName: eventNames.locationPermissionDenied,
          payload: {
            ...createSuperPropertiesForTrackingEvent({ useAuthData: user }),
            ...{
              errorMessage: geoLocationData.error.message
            },
            sourceInfo: getSourceInfoEvent(currentPlatform)
          }
        });
      }
    }, [geoLocationData]);

    const getCityNameFromGPS = async (
      lat: number,
      long: number,
      userToken: string
    ) => {
      try {
        const res = await getCityFromGPSCoordinates(lat, long, userToken);
        const cityValue = res.data?.city;
        setReverseGeoCodeResponse(res.data);

        if (cityValue) {
          setValue("city", cityValue);

          setLocationSource(UserCitySource.GPS);
        }
      } catch (error: unknown) {
        Sentry.captureException(error);
        addToast({
          heading: "Unable to get location",
          variant: "error"
        });
      }
    };

    const fetchCityList = async () => {
      try {
        const res = await fetchCityBySearchParams(getValues().city);

        const constructedData = res?.data.map((elem) => {
          return {
            name: elem.name,
            value: elem.name
          };
        });

        setCityList(constructedData);
      } catch (error) {
        Sentry.captureException(error);
        const err = error as { message: string };
        addToast({
          heading: err.message,
          variant: "error"
        });
      }
    };

    const fetchSchoolList = async () => {
      try {
        const res = await fetchSchoolBySearchParams(getValues().school);

        const constructedData = res?.data.map((elem) => {
          return {
            name: elem.name,
            value: elem.name
          };
        });

        setSchoolList(constructedData);
      } catch (error) {
        Sentry.captureException(error);
        const err = error as { message: string };
        addToast({
          heading: err.message,
          variant: "error"
        });
      }
    };

    const onSubmit: SubmitHandler<UpdateUserFormSchema> = async ({
      city,
      school,
      parentNumber
    }) => {
      setIsLoading.on();
      trackEvent({
        eventName: eventNames.submitClickedOnAdditionalDetailsScreen,
        payload: {
          ...createSuperPropertiesForTrackingEvent({ useAuthData: user }),
          ...{
            location: city,
            schoolName: school,
            parentMobile: parentNumber,
            citySource: locationSource ? locationSource : UserCitySource.Manual,
            sourceInfo: getSourceInfoEvent(currentPlatform)
          }
        }
      });

      // This will also create parent using parent phone number, it will pass name and salutation as empty while creating a parent

      const updateUserProfilePayload = {
        city,
        school,
        grade: primary_user.grade,
        class: primary_user.class_type,
        curriculum: primary_user.curriculum,
        stream: primary_user.stream,
        citySource: locationSource ? locationSource : UserCitySource.Manual,
        ...(locationSource === UserCitySource.GPS
          ? {
              administrativeAreaLevels:
                reverseGeoCodeResponse?.administrative_area_levels
            }
          : {})
      };

      const createParentPayload: ICreateParentPayload = {
        parent: {
          name: "",
          salutation: "",
          source: "onboarding",
          country_code: CountryCode.Indonesia,
          notification_consent: true,
          phone_number: parentNumber
        }
      };

      try {
        await updateUserProfile(updateUserProfilePayload, access_token);
        await createParentDetails(
          createParentPayload,
          access_token,
          student_id
        );

        setIsSuccessModalOpen(true);
        setIsLoading.off();
      } catch (err) {
        Sentry.captureException(err);
        const error = parseAPIErrorResponse(err);
        const { message } = error as IApiError;
        addToast({
          heading: message,
          variant: "error"
        });
        setIsLoading.off();
      }
    };

    return {
      control,
      cityList,
      schoolList,
      fetchCityList,
      fetchSchoolList,
      isValid,
      onSubmit,
      handleSubmit,
      isSuccessModalOpen,
      setIsSuccessModalOpen,
      getGPSData,
      geoLocationData,
      locationSource,
      setLocationSource,
      isLoading
    };
  };

export { useUpdateUserProfileForm };
